import { Decimal, Money } from "@station/core";
import { z } from "zod";
import { istanbulWeekday } from "./apply.js";
/**
 * Sadakat puanı (saf) — ADR 0017 §18–20, §23.
 *
 *  - **Kazanım SUNUCUDA hesaplanır** (worker `pos.sale.recorded` tüketicisi); kasa AYNI fonksiyonla yalnızca "kazanılacak puan"
 *    gösterir. Kurallar ana veriyle kasaya iner (`loyalty.earnRules`).
 *  - Satır başına EN ÖZEL temel kural (`PER_LITRE` / `PER_AMOUNT`) uygulanır: ürün > kategori > iş birimi > hepsi; eşitlikte
 *    `priority` küçük olan, sonra `code`. Eşleşen tüm `MULTIPLIER` kuralları ve seviye çarpanı çarpılır.
 *  - Satır puanı AŞAĞI yuvarlanır (tam sayı); satış puanı satır puanlarının toplamıdır.
 *  - Puanla ödenen kısım puan kazandırmaz: satırların neti ve miktarı `(sepet neti − puanla ödenen) / sepet neti` oranıyla
 *    küçültülür. Yaş sınırlı satırlar programda `excludeAgeRestricted` ise puan kazandırmaz (#67).
 *  - Puan ↔ tutar: `tutar = ROUND_HALF_UP(puan × puan değeri)` (puan değeri ₺, 4 ondalık).
 */
export const LOYALTY_EARN_RULE_KINDS = ["PER_LITRE", "PER_AMOUNT", "MULTIPLIER"];
export const loyaltyEarnRuleKindSchema = z.enum(LOYALTY_EARN_RULE_KINDS);
export const LOYALTY_EARN_RULE_KIND_LABELS = {
    PER_LITRE: "Litre / birim başına puan",
    PER_AMOUNT: "Harcanan her 1 ₺ için puan",
    MULTIPLIER: "Puan çarpanı",
};
export const LOYALTY_RULE_SCOPE_KINDS = ["ALL", "BUSINESS_UNIT", "CATEGORY", "PRODUCT"];
export const loyaltyRuleScopeKindSchema = z.enum(LOYALTY_RULE_SCOPE_KINDS);
export const LOYALTY_RULE_SCOPE_LABELS = {
    ALL: "Tüm satışlar",
    BUSINESS_UNIT: "İş birimi",
    CATEGORY: "Kategori",
    PRODUCT: "Ürün",
};
const SCOPE_SPECIFICITY = {
    PRODUCT: 3,
    CATEGORY: 2,
    BUSINESS_UNIT: 1,
    ALL: 0,
};
const pointsPerUnitSchema = z.string().regex(/^\d{1,9}(\.\d{1,4})?$/, "Puan, en çok 4 ondalık");
const multiplierSchema = z.string().regex(/^\d{1,3}(\.\d{1,3})?$/, "Çarpan, en çok 3 ondalık");
/** Puan değeri: ₺ / puan, 4 ondalık, sıfırdan büyük. */
export const loyaltyPointValueSchema = z
    .string()
    .regex(/^\d{1,6}(\.\d{1,4})?$/, "Puan değeri ₺ cinsinden, en çok 4 ondalık")
    .refine((value) => Decimal.from(value).isPositive(), "Puan değeri sıfırdan büyük olmalıdır");
export const loyaltyEarnRuleObjectSchema = z.object({
    id: z.uuid(),
    code: z.string().min(2).max(40),
    name: z.string().min(1).max(120),
    kind: loyaltyEarnRuleKindSchema,
    scopeKind: loyaltyRuleScopeKindSchema,
    /** `ALL` → boş; diğerleri → ürün / kategori kimliği ya da iş birimi kodu. */
    scopeIds: z.array(z.string().min(1)).max(500).default([]),
    /** `PER_LITRE`: litre (birim) başına; `PER_AMOUNT`: net 1 ₺ başına. `MULTIPLIER`'da `null`. */
    pointsPerUnit: pointsPerUnitSchema.nullable().default(null),
    /** Yalnızca `MULTIPLIER`: ör. "2.000" (hafta sonu iki kat). */
    multiplier: multiplierSchema.nullable().default(null),
    priority: z.number().int().min(0).max(1000).default(100),
    validFrom: z.iso.datetime({ offset: true }),
    validTo: z.iso.datetime({ offset: true }).nullable().default(null),
    /** 0 = Pazar … 6 = Cumartesi; boş = her gün. */
    weekdays: z.array(z.number().int().min(0).max(6)).default([]),
    isActive: z.boolean().default(true),
});
/** Çapraz alan kuralları (veritabanı CHECK'lerinin TS eşleniği). */
export function loyaltyEarnRuleIssues(input) {
    const issues = [];
    if (input.kind === "MULTIPLIER") {
        if (input.multiplier === null || !Decimal.from(input.multiplier).isPositive())
            issues.push({ path: "multiplier", message: "Çarpan kuralında sıfırdan büyük çarpan zorunludur" });
        if (input.pointsPerUnit !== null)
            issues.push({ path: "pointsPerUnit", message: "Çarpan kuralında puan / birim girilmez" });
    }
    else {
        if (input.pointsPerUnit === null || !Decimal.from(input.pointsPerUnit).isPositive())
            issues.push({ path: "pointsPerUnit", message: "Puan / birim sıfırdan büyük olmalıdır" });
        if (input.multiplier !== null)
            issues.push({ path: "multiplier", message: "Çarpan yalnızca çarpan kuralında girilir" });
    }
    if (input.scopeKind === "ALL" && input.scopeIds.length > 0)
        issues.push({ path: "scopeIds", message: "Tüm satışlar kapsamında kimlik girilmez" });
    if (input.scopeKind !== "ALL" && input.scopeIds.length === 0)
        issues.push({ path: "scopeIds", message: "Kapsam için en az bir seçim yapılmalıdır" });
    return issues;
}
export const loyaltyEarnRuleSchema = loyaltyEarnRuleObjectSchema.superRefine((value, context) => {
    for (const issue of loyaltyEarnRuleIssues(value))
        context.addIssue({ code: "custom", path: [issue.path], message: issue.message });
});
function ruleActiveAt(rule, at) {
    if (!rule.isActive)
        return false;
    const time = at.getTime();
    if (time < Date.parse(rule.validFrom))
        return false;
    if (rule.validTo && time >= Date.parse(rule.validTo))
        return false;
    if (rule.weekdays.length > 0 && !rule.weekdays.includes(istanbulWeekday(at)))
        return false;
    return true;
}
function ruleMatchesLine(rule, line) {
    switch (rule.scopeKind) {
        case "ALL":
            return true;
        case "BUSINESS_UNIT":
            return line.businessUnit != null && rule.scopeIds.includes(line.businessUnit);
        case "CATEGORY":
            return line.categoryId != null && rule.scopeIds.includes(line.categoryId);
        case "PRODUCT":
            return rule.scopeIds.includes(line.productId);
    }
}
/** En özel temel kural: kapsam özgüllüğü azalan, `priority` artan, `code` alfabetik. */
function pickBaseRule(rules, line) {
    const candidates = rules.filter((rule) => rule.kind !== "MULTIPLIER" && ruleMatchesLine(rule, line));
    candidates.sort((a, b) => SCOPE_SPECIFICITY[b.scopeKind] - SCOPE_SPECIFICITY[a.scopeKind] ||
        a.priority - b.priority ||
        (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
    return candidates[0] ?? null;
}
/** Satışın kazandıracağı puan. */
export function computeLoyaltyEarn(input) {
    const at = typeof input.at === "string" ? new Date(input.at) : input.at;
    const active = input.rules.filter((rule) => ruleActiveAt(rule, at));
    const basketNet = Decimal.sum(input.lines.map((line) => line.net));
    const pointsPaid = Decimal.from(input.pointsPaidAmount ?? "0");
    // Puanla ödenmeyen pay (0–1). Sepet neti sıfırsa kazanım da sıfırdır.
    const factor = basketNet.isPositive()
        ? Decimal.max(Decimal.ZERO, basketNet.sub(Decimal.min(pointsPaid, basketNet)).div(basketNet))
        : Decimal.ZERO;
    const tierMultiplier = Decimal.from(input.tierMultiplier ?? "1");
    const lines = input.lines.map((line) => {
        const excluded = (input.excludeAgeRestricted ?? true) && line.ageRestricted === true;
        const base = excluded ? null : pickBaseRule(active, line);
        const earnableNet = Decimal.from(line.net).mul(factor);
        const earnableQuantity = Decimal.from(line.quantity).mul(factor);
        if (!base || Decimal.from(line.net).lte(0))
            return {
                lineNo: line.lineNo,
                ruleId: null,
                multiplierRuleIds: [],
                earnableNet: earnableNet.round(0).toFixed(0),
                earnableQuantity: earnableQuantity.round(3).toFixed(3),
                rawPoints: "0.0000",
                points: "0",
            };
        const multipliers = active.filter((rule) => rule.kind === "MULTIPLIER" && ruleMatchesLine(rule, line));
        const units = base.kind === "PER_LITRE" ? earnableQuantity : earnableNet.div(100);
        let raw = units.mul(base.pointsPerUnit);
        for (const rule of multipliers)
            raw = raw.mul(rule.multiplier);
        raw = raw.mul(tierMultiplier);
        return {
            lineNo: line.lineNo,
            ruleId: base.id,
            multiplierRuleIds: multipliers.map((rule) => rule.id),
            earnableNet: earnableNet.round(0).toFixed(0),
            earnableQuantity: earnableQuantity.round(3).toFixed(3),
            rawPoints: raw.round(4).toFixed(4),
            points: Decimal.max(Decimal.ZERO, raw.floor(0)).toFixed(0),
        };
    });
    return { lines, points: Decimal.sum(lines.map((line) => line.points)).toFixed(0) };
}
/**
 * İade / iptal karşı kaydında geri alınacak puan: her iade satırı için `satır puanı × iade miktarı / satır miktarı`,
 * ROUND_HALF_UP, satır puanını AŞAMAZ. Tam iptalde (tüm miktar) toplam kazanımın aynısıdır.
 */
export function computeEarnReversal(earned, returned) {
    const byLine = new Map(earned.map((line) => [line.lineNo, line]));
    let total = Decimal.ZERO;
    for (const item of returned) {
        const line = byLine.get(item.lineNo);
        if (!line || !Decimal.from(line.quantity).isPositive())
            continue;
        const share = Decimal.from(line.points)
            .mul(Decimal.from(item.quantity).abs())
            .div(line.quantity)
            .round(0);
        total = total.add(Decimal.min(share, line.points));
    }
    return total.toFixed(0);
}
/** Puanın ₺ karşılığı (kuruş): `ROUND_HALF_UP(puan × puan değeri)`. */
export function pointsToAmount(points, pointValue) {
    return Money.fromDecimalMajor(Decimal.from(points).mul(pointValue));
}
/**
 * Tutarı AŞMAYAN en çok puan: `pointsToAmount(p) ≤ tutar` olan en büyük `p`. Yarım yukarı yuvarlama yüzünden bu
 * `⌊tutar / değer⌋`'den büyük olabilir (değer < 1 kuruşsa birkaç puan aynı kuruşa düşer): `p × değer × 100 < tutar + 0,5`
 * ⇔ `p = ⌊(tutar + 0,5) / (değer × 100)⌋`, sınır tam denk gelirse (yukarı yuvarlanır) bir eksiği.
 */
export function amountToMaxPoints(amountKurus, pointValue) {
    if (Decimal.from(amountKurus).isNegative())
        return "0";
    const points = Decimal.from(amountKurus).add("0.5").div(Decimal.from(pointValue).mul(100)).floor(0);
    return pointsToAmount(points.toFixed(0), pointValue).gt(Money.fromMinor(amountKurus))
        ? Decimal.max(Decimal.ZERO, points.sub(1)).toFixed(0)
        : points.toFixed(0);
}
/** Puanla ödenebilecek satırların neti (kuruş). */
export function redeemableNet(lines, program) {
    return Decimal.sum(lines
        .filter((line) => (program.redeemableBusinessUnits.length === 0 ||
        (line.businessUnit != null && program.redeemableBusinessUnits.includes(line.businessUnit))) &&
        !(program.excludeAgeRestricted && line.ageRestricted === true))
        .map((line) => line.net)).toFixed(0);
}
export const POINTS_REDEMPTION_REASONS = [
    "NO_CUSTOMER",
    "REDEEM_DISABLED",
    "ACCOUNT_BLOCKED",
    "NOT_INTEGER",
    "BELOW_MINIMUM",
    "AMOUNT_MISMATCH",
    "EXCEEDS_REDEEMABLE",
    "INSUFFICIENT",
];
/** Puan ödemesinin denetimi — ajan (Go eşleniği) ve kasa aynı sırayla; ilk başarısızlık döner. */
export function validatePointsRedemption(input) {
    const balance = Decimal.max(Decimal.ZERO, Decimal.from(input.balance));
    const byAmount = Decimal.from(amountToMaxPoints(input.redeemableNet, input.program.pointValue));
    let maxPoints = Decimal.min(balance, byAmount);
    if (maxPoints.lt(input.program.minRedeemPoints))
        maxPoints = Decimal.ZERO;
    const result = (reason) => ({
        ok: reason === null,
        reason,
        maxPoints: maxPoints.toFixed(0),
    });
    if (!input.hasCustomer)
        return result("NO_CUSTOMER");
    if (!input.program.redeemEnabled)
        return result("REDEEM_DISABLED");
    if (input.accountBlocked)
        return result("ACCOUNT_BLOCKED");
    if (!/^\d{1,15}$/.test(input.points) || !Decimal.from(input.points).isPositive())
        return result("NOT_INTEGER");
    const points = Decimal.from(input.points);
    if (points.lt(input.program.minRedeemPoints))
        return result("BELOW_MINIMUM");
    if (!pointsToAmount(input.points, input.program.pointValue).eq(Money.fromMinor(input.amount)))
        return result("AMOUNT_MISMATCH");
    if (Decimal.from(input.amount).gt(input.redeemableNet))
        return result("EXCEEDS_REDEEMABLE");
    if (points.gt(balance))
        return result("INSUFFICIENT");
    return result(null);
}
//# sourceMappingURL=loyalty.js.map